import { useState } from 'react'
import { Dialog, DialogHeader, DialogTitle } from '../../../../components/ui/dialog'
import { useFetchReviews } from '../../data'
import { useReviewsConfig } from '../../providers'
import { ReviewsGrid } from '../reviews-grid'
import { ReviewStars } from './review-stars'
import ResponsiveDialogContent from '../../../../components/ResponsiveDialogContent'
import { filterReviews } from '../../helpers/utils'
import { useReviewsCss } from '../../hooks/useReviewsCss'

export function AllReviewsSection() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const options = useReviewsConfig()
  const cssString = useReviewsCss()

  const { data, isLoading, isError, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useFetchReviews(options.filter)

  const reviews = filterReviews(data?.pages?.flatMap(page => (page.result || [])) || [], options.searchQuery)
  const totalReviews = data?.pages?.[0]?.pagination?.total_items || 0
  const averageRating = data?.pages?.[0]?.pagination?.average_rating

  return (
    <>
      <div className="homerunner-all-reviews-section flex justify-center mt-10">
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-accent text-primary-foreground hover:bg-accent/90 px-6 py-3 rounded-md font-medium transition-colors shadow"
        >
          View All Reviews
        </button>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <ResponsiveDialogContent
          className={`homerunner-all-reviews-modal h-[90vh] bg-background border border-border shadow-lg ${options.resolvedTheme === 'dark' ? 'dark' : ''}`}
          elevateDom
          cssString={cssString}
          style={{
            '--content-max-width': '1280px',
            '--masonry-columns-mobile': '1',
            '--masonry-columns-tablet': '2',
            '--masonry-columns-desktop': '3',
          } as React.CSSProperties}
        >
          <DialogHeader className="flex-shrink-0 pb-4 sm:pb-6 px-0">
            <div className="flex flex-col items-center sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
              <DialogTitle className="text-base sm:text-2xl font-bold text-foreground text-center sm:text-left">
                All Reviews ({totalReviews.toLocaleString()})
              </DialogTitle>
              {averageRating && !options.hideOverallRating && (
                <div className="flex items-center gap-2 sm:gap-3 text-muted-foreground sm:ml-auto">
                  <span className="text-base sm:text-2xl font-semibold text-foreground">
                    {averageRating.toFixed(1)}
                  </span>
                  <ReviewStars rating={averageRating} starClass="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              )}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 px-0">
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                      <div className="h-4 bg-muted-foreground/20 rounded w-3/4"></div>
                      <div className="h-4 bg-muted-foreground/20 rounded w-1/2"></div>
                      <div className="h-4 bg-muted-foreground/20 rounded w-full"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div className="text-center text-destructive py-8">
                Error loading reviews: {error?.message}
              </div>
            ) : (
              <div className="space-y-6">
                <ReviewsGrid
                  reviews={reviews}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  onLoadMore={() => fetchNextPage()}
                  gridType="masonry"
                  columnCount={1}
                  columnCount780={2}
                  columnCount1200={3}
                />
              </div>
            )}
          </div>
        </ResponsiveDialogContent>
      </Dialog>
    </>
  )
}
